import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ahuBoardApi, useGetAdpGageCmsParamsQuery } from 'app/services/ahuBoardApi';
import { ahuBoardFormsApi } from 'app/services/ahuBoardFormsApi';
import { resetAhuBoard, setColorMode } from 'app/slices/ahuBoardSlice';
import { useStylesheets } from 'hooks/useStylesheets';
import './ahuBoardOverrides.scss';
import { usePopupKeys } from 'hooks/usePopupKeys';
import { getBootUserId } from 'hooks/useDashboardData';
import GlobalHeader from 'pages/dashboard/components/GlobalHeader';
import WorkflowPopup from 'pages/project-board/components/workflow/WorkflowPopup';
import { openWorkflow, closeWorkflow } from 'app/slices/projectBoardSlice';
import { useAhuBoardBootstrap } from './hooks/useAhuBoardBootstrap';
import { useAhuPermissions, isPowerUser } from './hooks/useAhuPermissions';
import { useBodyZoom } from './hooks/useBodyZoom';
import { useAhuBoardSyncdata } from './hooks/useAhuBoardSyncdata';
import { ahuBoardBus, registerNoopCanvasConsumers } from './bus/ahuBoardBus';
import { readCanvasColorMode } from './ahuBoardStorage';
import { useValidationManager } from './validation/useValidationManager';
import { resetSectionPatchQueue } from './forms/sectionPatchQueue';
import {
  createComponentAny,
  createAccessoryAny,
  duplicateAccessoryAny,
  type CreateComponentBlock,
} from './boardActions';
import type { CanvasTunnel } from './three/canvas3d';
import {
  buildAccessoryBootDraw,
  buildCoilBootDraw,
  buildEvapBootDraw,
  type AccessoryDrawArgs,
  type CanvasBootDraws,
} from './three/bootDrawFeeds';
import { lugsOnForTunnel } from './hybridLugFields';
import AhuNameBreadcrumb from './components/AhuNameBreadcrumb';
import BoardContent from './components/BoardContent';
import AhuBoardFooter from './components/AhuBoardFooter';
import { usePsychrometrics } from './psychrometric/usePsychrometrics';
import PsychrometricChartPopup from './psychrometric/PsychrometricChartPopup';

/**
 * AHU board CSS — Part 5 §43 cascade (parity 4), ONE useStylesheets call:
 * header.css → ahu/skeleton.css → common.css → ahu/styles.css. react.html
 * loads common.css + header.css globally; re-injecting all four here (in
 * order, appended after the globals) reproduces the exact legacy cascade —
 * what matters is the ORDER of the last occurrence of each sheet.
 * styles.css bundles Bootstrap 3.3.4 + the Material-Icons @import (§43) so it
 * MUST stay route-scoped.
 */
const AHU_CSS = [
  '/static/css/common/header.css',
  '/static/css/ahu/skeleton.css',
  '/static/css/common/common.css',
  '/static/css/ahu/styles.css',
];

/**
 * AHU Board page (/ahu/detail/:ahuId — Backbone app `fn.app.name === 'ahu'`),
 * sub-phases 6a (shell + data) + 6b (three.js bootstrap): #canvas_section now
 * mounts the route-split <AhuCanvas> (empty scene → camera-preset parity; the
 * section geometry is 6c); left-panel tab bodies, toolbar window contents,
 * insert strip and popups are empty mounts for 6e.
 *
 * Route shape per Part 1 §1.1 CORRECTION: `/ahu/detail/:ahuId` (no trailing
 * slash), parity 1. Unauthenticated access redirects at the Django layer
 * (ReactAppAuthView → /login/, parity 2).
 *
 * Boot parity in this component:
 *  - parity 3/197: document.title 'AHU' → `AHU: <project name lowercased>_<tag>`
 *    on project fetch success (model.project side effect).
 *  - parity 6: body[data-user_id].
 *  - parity 7: `.preloader.dotted-preloader` inside #ahu-board-content until
 *    the shell data resolves; BoardContent's own preloader then holds until
 *    the 6b canvas boot (font + WebGL context) collapses it.
 *  - parity 9: simulateZoomApp body zoom at boot + resize (useBodyZoom).
 *  - parity 10/14: permission body classes incl. the project-driven downgrade
 *    (recomputed after workflow:done → project refetch).
 *  - parity 12: `.static_version` on #ahu-board-content.
 *  - parity 13: selection store initial `{ahu, null, []}` (slice initialState).
 *  - parity 78: sessionStorage `visited` is marked by the 6b canvas boot
 *    (AhuCanvas) at the legacy tunnel.js timing; the refresh-restore CONSUMER
 *    is 6d.
 *  - §25 bus: live; the canvas channels are consumed by <AhuCanvas> (6b) —
 *    camera ops are real, draw channels drain the replay buffers as no-ops
 *    until 6c. The page-level passive no-ops remain as a safety net for the
 *    pre-mount window.
 */
const AhuBoardPage = () => {
  const { ahuId: ahuIdParam } = useParams();
  const ahuId = Number(ahuIdParam);
  const dispatch = useAppDispatch();
  const workflowOpen = useAppSelector((s) => s.projectBoard.modals.workflow);

  useStylesheets(AHU_CSS);
  // ESC closes the active popup / Enter clicks button[autofocus] (§2.4 body
  // keyup — parity 29 baseline; popup-specific .active handling lands with
  // each popup port).
  usePopupKeys();
  // Body zoom hack (R2) — boot + resize (parity 9).
  useBodyZoom();

  const {
    user,
    ahu,
    tunnels,
    sectionsByTunnel,
    dmountsBySection,
    componentMetaBySection,
    coilFeedsBySection,
    evapFeedsBySection,
    intWallsBySection,
    accessoriesBootBySection,
    lugsByTunnel,
    floorLabelByTunnel,
    project,
    permissions,
    constructionClasses,
    cms,
    shellReady,
    ahuError,
  } = useAhuBoardBootstrap(Number.isFinite(ahuId) ? ahuId : null);

  // Permission body classes (parity 10/11/14).
  const ahuPermissions = useAhuPermissions(permissions, project, user, ahu);
  // View-only *purely* because this is a hybrid AHU and the user isn't a Power
  // user — the one downgrade that leaves the Drawing window's Settings /
  // Callout Schedule blocks editable (they drive Model View output).
  const hybridStandardLock = !!ahuPermissions?.hybridStandardLock;
  // Hybrid AHU write gate: only a Power user may edit a hybrid AHU's
  // per-tunnel addressing/base/lug fields (backend-enforced; this only
  // hides/disables the controls client-side).
  const userIsPowerUser = isPowerUser(user);

  // Bus lifecycle: passive no-op canvas consumers mark the 6b/6c attach point;
  // full reset on unmount (fresh-boot semantics, no cross-mount leaks — R8).
  useEffect(() => {
    const off = registerNoopCanvasConsumers();
    return () => {
      off();
      ahuBoardBus.reset();
      resetSectionPatchQueue();
      dispatch(resetAhuBoard());
    };
  }, [dispatch]);

  // Dev-sync J1 — psychrometric chart host (header launch + create-once
  // popup + page-load minimize replay). The popup itself portals to <body>
  // below, like legacy's $("body").append (header.js:561).
  const psychrometrics = usePsychrometrics();

  // 6e1 — validation manager: boot validate_all_3D kick + bus receivers +
  // THE error→tint producer (§22.5; gates generate + #li_error_log).
  const tunnelIds = useMemo(
    () => (tunnels ? tunnels.map((t) => t.id) : null),
    [tunnels]
  );
  useValidationManager(tunnelIds);

  // Syncdata websocket → section geometry (Backbone parity:
  // ahu/mvc/fn/syncdata_listener.js). This is the channel that carries the
  // section length the backend recomputes during a component SELECT, and
  // therefore what makes the canvas redraw afterwards.
  useAhuBoardSyncdata(Number.isFinite(ahuId) ? ahuId : null, tunnelIds);

  // 6e1 — §25.1 auto-wires (api.js): create:component:any /
  // create:accessory:any / duplicate:accessory:any.
  useEffect(() => {
    if (!ahu) return undefined;
    const offs = [
      ahuBoardBus.on('create:component:any', (blocks) => {
        void dispatch(
          createComponentAny(blocks as unknown as CreateComponentBlock[], { ahuId: ahu.id })
        );
      }),
      ahuBoardBus.on('create:accessory:any', (name) => {
        void dispatch(createAccessoryAny(name));
      }),
      ahuBoardBus.on(
        'duplicate:accessory:any',
        (sectionId, type, model, position, sameSection, tunnelId) => {
          void dispatch(
            duplicateAccessoryAny(sectionId, type, model, position, sameSection, tunnelId)
          );
        }
      ),
    ];
    return () => offs.forEach((offFn) => offFn());
  }, [ahu, dispatch]);

  // Side-channel boot read: color mode ([NEG]#9 — comparison, not eval).
  // sessionStorage `visited` (parity 78) is marked by AhuCanvas at the legacy
  // tunnel.js timing (after the restore decision point).
  useEffect(() => {
    dispatch(setColorMode(readCanvasColorMode()));
  }, [dispatch]);

  // body[data-user_id] (server-shell parity 6).
  useEffect(() => {
    const id = getBootUserId();
    if (id != null) document.body.setAttribute('data-user_id', String(id));
    return () => {
      document.body.removeAttribute('data-user_id');
    };
  }, []);

  // Height-chain parity (6c1 fix round 1, finding 4): the legacy board chain
  // is html(100%) → body → #ksdm-content `calc(100% - 90px)` (ahu
  // skeleton.css:173) → #ahu-board-content → … → 917px @1080. The React shell
  // breaks it twice:
  //  (1) #react-root has no height → skeleton's percentage resolves to auto;
  //  (2) the dashboard bundle leaks `body #ksdm-content{height:calc(100vh -
  //      53px)}` (StatusesRail.module.scss :global) which out-ranks skeleton's
  //      `#ksdm-content` everywhere — 31px too tall @1920×1080 (footer pushed
  //      off-screen) and, because vh ignores the body-zoom hack, 86px too
  //      short @1600×900.
  // Route-scoped repair: pin #react-root to 100% for the board's lifetime so
  // the legacy %-chain (and its body-zoom semantics) works, and carry
  // skeleton.css's own value as an inline style on #ksdm-content (below) to
  // out-rank the leaked vh rule without touching the dashboard/project-board
  // sheets.
  useEffect(() => {
    const root = document.getElementById('react-root');
    const prev = root?.style.height ?? '';
    if (root) root.style.height = '100%';
    return () => {
      if (root) root.style.height = prev;
    };
  }, []);

  // parity 3/197 — base 'AHU …' then the project-fetch title side effect.
  useEffect(() => {
    if (project?.name && ahu?.tag != null) {
      document.title = `AHU: ${String(project.name).toLowerCase()}_${ahu.tag}`;
    } else {
      document.title = 'AHU Kinetics sdm';
    }
  }, [project?.name, ahu?.tag]);

  // Bad id / no access → the legacy board has no client fallback (Django 404s);
  // the React shell sends the user back to the dashboard like project-board.
  useEffect(() => {
    if (ahuError || !Number.isFinite(ahuId)) window.location.replace('/dashboard');
  }, [ahuError, ahuId]);

  // Flat section list for navy-button gating (§7.2).
  const sections = useMemo(
    () => (sectionsByTunnel ? Object.values(sectionsByTunnel).flat() : []),
    [sectionsByTunnel]
  );

  /**
   * Hybrid AHU — hold the drawing area when the last section is deleted.
   *
   * Deleting it drops the AHU's only tunnel to zero length, which trips the
   * board's empty state (`BoardContent`'s `isEmpty`): the "Build your Air
   * Handling Unit" card covers the canvas and the canvas rail is hidden, so
   * the whole drawing disappears mid-edit. Requested 2026-10-02: keep the
   * empty canvas — rail, ruler and all — until the page is reloaded or a
   * section is added back.
   *
   * The ruler survives on its own: `drawRuler` only runs from the
   * section-render pass, so with nothing left to draw the group built for the
   * last full render simply stays in the scene. All this does is stop the
   * card from covering it.
   *
   * Armed by the delete (`SectionToolbarChrome`), not derived from
   * `(sections, is_hybrid)` — those arrive from separate refetches and the
   * order is not fixed, so a derived flag dropped the hold whenever the unit
   * landed before the section lists. Held per AHU id so it cannot leak across
   * a board switch, and only while the AHU really is empty; a page load starts
   * it unset, which is what "until the user refreshes the page" means.
   */
  const [holdEmptyCanvasFor, setHoldEmptyCanvasFor] = useState<number | null>(null);
  useEffect(() => {
    if (sections.length) setHoldEmptyCanvasFor(null);
  }, [sections.length]);
  const holdEmptyCanvas =
    holdEmptyCanvasFor != null && holdEmptyCanvasFor === ahu?.id && sections.length === 0;

  // 6d — fn['current.cms.adp_gage'] re-home (canvas.view3d:23/:56-58): the
  // ADP_GAGE canvas-tooltip manufacturer names. Legacy blocks the canvas boot
  // on this fetch; React fetches in parallel (session-cached) — documented
  // dedrift (only the tooltip mfg text is affected pre-resolve).
  const { data: adpGageCms } = useGetAdpGageCmsParamsQuery();
  const adpGageMfgs = useMemo<Record<number, string> | null>(() => {
    const manufacturers = adpGageCms?.adp_gage?.manufacturer;
    if (!manufacturers) return null;
    const map: Record<number, string> = {};
    manufacturers.forEach((m) => {
      map[m.id] = m.name;
    });
    return map;
  }, [adpGageCms]);

  // Canvas tunnel rows: the legacy model/tunnel.js derivation of
  // `current_index_values` — CMS tunnels.indexes value '2_1' → [2, 1]
  // ([1] < [0] = side-by-side, drives next_to / world scale, R5). Falls back
  // to [1, 1] (stacked semantics) when the CMS row is missing — legacy would
  // throw in get_index(); the canvas must not. 6c1 adds the per-tunnel
  // section rows (collection order = order_rate asc, §21.3 comparator), the
  // `base` object (G6) and under_insulation_thick (G4).
  const canvasTunnels = useMemo<CanvasTunnel[]>(() => {
    if (!tunnels) return [];
    const indexes = cms?.tunnels?.indexes ?? [];
    return tunnels.map((t) => {
      const idx = indexes.find((i) => i.id === t.index_id);
      const civ = (idx?.value ?? '')
        .split('_')
        .map((n) => parseInt(n, 10))
        .filter((n) => Number.isFinite(n));
      const base = t.base as { height?: number | null; type?: string | null } | null | undefined;
      const sectionRows = [...(sectionsByTunnel?.[t.id] ?? [])]
        .sort((a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0))
        .map((s) => ({
          id: s.id,
          length: Number(s.length) || 0,
          name: s.name ?? null,
          tag: s.tag ?? null,
          type: s.type ?? null,
          // Transient create-flow stamp — null on boot, written by
          // createComponentAny on insert (api.js:70-72/:253).
          name_on_canvas: s.name_on_canvas ?? null,
          order_rate: s.order_rate ?? 0,
          sitting_on_id: s.sitting_on_id ?? null,
          aw_width: Number(s.aw_width) || 0,
          aw_height: Number(s.aw_height) || 0,
          casing_insulation_thick: Number(s.casing_insulation_thick) || 0,
          required_length: s.required_length == null ? null : Number(s.required_length) || 0,
          // G33 component-model probes (§20.5 #43→#47 bootstrap fetches);
          // undefined while in flight = the legacy pre-fetch stateStorage
          // state (neutral arrows / no fan-tunnel arrow until loaded).
          // wheel_configuration_id stays on the legacy boot fallback
          // (`next_to ? 2 : 1`) — see CanvasSection doc.
          coil_duty_id: componentMetaBySection?.[s.id]?.coil_duty_id,
          fan_orientation_id: componentMetaBySection?.[s.id]?.fan_orientation_id,
          // getSectionNameColor UDC probe (6c2 — §20.5 #47 udc fetch);
          // undefined while in flight = legacy pre-fetch default (red label).
          udc_has_name: componentMetaBySection?.[s.id]?.udc_has_name,
          // getSectionNameColor gas-heater probe (§20.5 #47 gas heater fetch);
          // undefined while in flight = red label, same as the UDC probe.
          gh_has_model: componentMetaBySection?.[s.id]?.gh_has_model,
          // G29 dmount rows (§20.4 #38); null while in flight = legacy
          // pre-listener state (no dmount dims yet).
          dmounts:
            dmountsBySection == null
              ? null
              : (dmountsBySection[s.id] ?? []).map((d) => ({
                  id: d.id,
                  dist_from: Number(d.dist_from) || 0,
                  plane_id: d.plane_id ?? null,
                })),
        }));
      return {
        id: t.id,
        width: Number(t.width) || 0,
        height: Number(t.height) || 0,
        length: Number(t.length) || 0,
        airflow_direction_id: t.airflow_direction_id ?? null,
        index: t.index ?? '',
        baseHeight: Number(base?.height) || 0,
        current_index_values: (civ.length === 2 ? [civ[0], civ[1]] : [1, 1]) as [number, number],
        base: base ? { height: base.height == null ? null : Number(base.height), type: base.type ?? null } : null,
        under_insulation_thick: Number(t.under_insulation_thick) || 0,
        // 6c2 — G7-G10 gates (drawTunnelBase lug replay / redrawTunnelLugs).
        // Resolved here rather than in the geometry, which has no AHU: legacy's
        // three canvas lug gates (canvas.view3d:1980/1991/4508) all read
        // `getHybridLugFieldValue('lug', tunnel, ahu)`, so on a hybrid AHU the
        // flag is the unit's. Feeding the tunnel's own column drew no lugs at
        // all there — it is false on every hybrid tunnel.
        lug: lugsOnForTunnel(ahu, t),
        base_id: t.base_id ?? null,
        // 6c2 — isCube plane-1 probe (`tunnel.floor.under_insulation_label`);
        // undefined while the floor fetch is in flight (legacy pre-fetch state).
        floor_under_insulation_label: floorLabelByTunnel?.[t.id],
        sections: sectionRows,
        // Hybrid AHU (KA-x) — is_hybrid is the AHU's own flag, not derived
        // from this tunnel's row/level/position (every tunnel carries those
        // regardless of whether the AHU is hybrid).
        is_hybrid: !!ahu?.is_hybrid,
        row: t.row,
        level: t.level,
        position: t.position,
        has_upstream_end_wall: t.has_upstream_end_wall,
        has_downstream_end_wall: t.has_downstream_end_wall,
        has_left_side_wall: t.has_left_side_wall,
        has_right_side_wall: t.has_right_side_wall,
        has_roof: t.has_roof,
        x_offset: t.x_offset,
        tunnel_offset_direction: t.tunnel_offset_direction,
        tunnel_offset: t.tunnel_offset,
      };
    });
  }, [tunnels, cms, sectionsByTunnel, dmountsBySection, componentMetaBySection, floorLabelByTunnel, ahu?.is_hybrid, ahu?.lug]);

  // construction-class → floor_construction_id (tunnel.js:492 re-home, R6/R15).
  const floorConstructionId = useMemo(() => {
    const cc = constructionClasses?.find((c) => c.id === ahu?.construction_class_id);
    return cc?.floor_construction_id ?? null;
  }, [constructionClasses, ahu?.construction_class_id]);

  // Section-catalog warm-up — the legacy pageinated.collection GLOBAL_CACHE
  // equivalent: tab.section/tab.multiply fetch these page-1 lists once per
  // board life and keep them for the page lifetime. Prefetching at boot makes
  // the sections tab paint stored values immediately instead of '-----' while
  // six serial fetches resolve (verify 6e2a-10 Casing Insulation regression);
  // held subscriptions pin the cache like the legacy global cache does.
  const boardCcid = ahu?.construction_class_id ?? null;
  const boardMfgId = user?.mfg_id ?? 1;
  useEffect(() => {
    if (boardCcid == null) return undefined;
    const paths = [
      'ahu/casing/metal_sheets',
      'ahu/floor/finishes',
      'ahu/casing/insulations',
      'ahu/casing/liner_metal_sheets',
      'ahu/floor/metal_sheets',
    ];
    const subs = paths.map((path) =>
      dispatch(
        ahuBoardFormsApi.endpoints.getCmsPaginated.initiate({
          mfgId: boardMfgId,
          path,
          page: 1,
          constructionClassId: boardCcid,
        })
      )
    );
    return () => subs.forEach((s) => s.unsubscribe());
  }, [boardCcid, boardMfgId, dispatch]);

  // 6c2 — boot-draw feeds: the bus payloads the legacy FORM layer emits while
  // rendering at boot (G11 int-walls via the storage poll, G12-G21 accessory
  // drawOnCanvas §49.6, G31 coil rated-model init, G32 evap drawConnections).
  // null until every feed resolved — AhuCanvas replays them once after the
  // first render pass (the bus buffers cover the pre-subscribe window).
  const bootDraws = useMemo<CanvasBootDraws | null>(() => {
    if (
      !sectionsByTunnel ||
      !intWallsBySection ||
      !accessoriesBootBySection ||
      !coilFeedsBySection ||
      !evapFeedsBySection
    )
      return null;
    const intWallDraws = Object.values(intWallsBySection).flat();
    const accessoryDraws: AccessoryDrawArgs[] = [];
    Object.entries(accessoriesBootBySection).forEach(([sectionId, rows]) => {
      rows.forEach((bootRow) => {
        const args = buildAccessoryBootDraw(
          bootRow.type,
          bootRow,
          intWallsBySection[Number(sectionId)] || []
        );
        if (args) accessoryDraws.push(args);
      });
    });
    const coilDraws = Object.values(coilFeedsBySection)
      .map((feed) => buildCoilBootDraw(feed.coil, feed.rated))
      .filter((d): d is NonNullable<typeof d> => !!d);
    const evapDraws = Object.entries(evapFeedsBySection)
      .map(([sid, evap]) => {
        const data = buildEvapBootDraw(evap);
        return data ? { sectionId: Number(sid), data } : null;
      })
      .filter((d): d is NonNullable<typeof d> => !!d);
    return { intWallDraws, accessoryDraws, coilDraws, evapDraws };
  }, [
    sectionsByTunnel,
    intWallsBySection,
    accessoriesBootBySection,
    coilFeedsBySection,
    evapFeedsBySection,
  ]);

  // #workflow_popup_button (boot step 4, fn['bind.view.workflow_popup']):
  // fresh project fetch first, popup opens on success. On close, refetch the
  // project so the permission downgrade re-evaluates (workflow:done parity 14).
  const onWorkflowClick = () => {
    const projectId = ahu?.project_id;
    if (projectId == null) return;
    dispatch(
      ahuBoardApi.endpoints.getAhuBoardProject.initiate(projectId, { forceRefetch: true })
    )
      .unwrap()
      .then(() => dispatch(openWorkflow()))
      .catch(() => undefined);
  };

  const onWorkflowClose = () => {
    dispatch(closeWorkflow());
    ahuBoardBus.emit('workflow:done');
    if (ahu?.project_id != null) {
      dispatch(ahuBoardApi.util.invalidateTags([{ type: 'AhuProject', id: ahu.project_id }]));
    }
  };

  return (
    <>
      {/* Generic popup host (server shell, ahuBase.html). */}
      <div id="wrapper-div" className="popup popup-wrapper" />

      {/* Workflow Manager popup (shared /static/mvc/ feature — Phase-3 port). */}
      {workflowOpen && ahu?.project_id != null && (
        <WorkflowPopup projectId={ahu.project_id} onClose={onWorkflowClose} />
      )}

      {/* Shared header — app name 'ahu' (parity 5). */}
      <GlobalHeader
        appName="ahu"
        onWorkflowClick={onWorkflowClick}
        onPsychrometricsClick={psychrometrics.open}
        psychrometricsEnabled={psychrometrics.enabled}
      />

      {/* height: skeleton.css:173's own value, inlined to beat the leaked
          dashboard `body #ksdm-content` vh rule (see height-chain effect). */}
      <div id="ksdm-content" style={{ height: 'calc(100% - 90px)' }}>
        <div id="ahu-tabs" data-id={Number.isFinite(ahuId) ? ahuId : undefined} className="clearfix">
          {/* Render-gate (QA batch 8 #5 FOUC, 69e552189c pattern): the legacy
              shell ships #ahu-name EMPTY (common/ahuBase.html:32) and main.js
              builds ViewProjectName only after project+AHU load (:276-283). */}
          {project && ahu ? (
            <AhuNameBreadcrumb
              ahuId={ahuId}
              ahuTag={ahu.tag}
              ahuIsHybrid={ahu.is_hybrid}
              project={project}
              permissions={permissions}
            />
          ) : (
            <div id="ahu-name" className="left" />
          )}
        </div>
        {/* Legacy new_ahu mount point (§10.3) — AhuNameBreadcrumb portals the
            "Create New..." popup here, like new_ahu.js's setElement. */}
        <div id="new_ahu_popup" />
        <div
          id="ahu-board-content"
          className={ahu?.static_version ? 'static_version' : ''}
        >
          {shellReady && ahu && tunnels ? (
            <BoardContent
              ahu={ahu}
              tunnels={tunnels}
              sections={sections}
              canvasTunnels={canvasTunnels}
              // First-draw gate: sections AND the CMS doc (6c1 fix round 1).
              // The legacy boot order guarantees the CMS tunnel indexes exist
              // before any tunnel model does (model/tunnel.js get_index);
              // drawing with the civ fallback [1,1] while `tunnel.index` says
              // '1:2' desyncs the tunnelsByIndexes keys and the first draw
              // throws in calculateTunnelBasePosition (dead canvas — boot
              // race seen on AHU 3128).
              sectionsReady={sectionsByTunnel != null && cms != null}
              // The remaining `canvasTunnels` inputs (memo deps above) — each
              // lands in its own tick after the first draw, and each is a
              // geometry change. AhuCanvas coalesces the boot window into one
              // pass on the strength of this; see its data-change effect.
              canvasDataSettled={
                dmountsBySection != null &&
                componentMetaBySection != null &&
                floorLabelByTunnel != null
              }
              floorConstructionId={floorConstructionId}
              lugsByTunnel={lugsByTunnel}
              bootDraws={bootDraws}
              ahuId={ahu.id}
              adpGageMfgs={adpGageMfgs}
              cms={cms}
              constructionClasses={constructionClasses}
              mfgId={user?.mfg_id ?? 1}
              isPowerUser={userIsPowerUser}
              hybridStandardLock={hybridStandardLock}
              holdEmptyCanvas={holdEmptyCanvas}
              onBoardEmptied={() => setHoldEmptyCanvasFor(ahu.id)}
            />
          ) : (
            // Server-shell preloader (parity 7) — removed when the shell data
            // resolves; BoardContent's own preloader then holds until the 6b
            // canvas boot (font + WebGL context) collapses it.
            <div className="preloader dotted-preloader" />
          )}
        </div>
      </div>

      {/* "New popup removes old" host (server shell). */}
      <div id="popup-only" />

      {/* Psychrometric chart popup (Dev-sync J1) — appended to <body> like
          legacy (header.js:561), create-once/never-destroy per page life
          (openPsychrometricChart reuses self.psy_popup on every later
          click). Mounted only by a button click or the stored-minimize
          replay — never eagerly. */}
      {psychrometrics.mounted &&
        createPortal(
          <PsychrometricChartPopup
            ahuId={Number.isFinite(ahuId) ? ahuId : null}
            boot={psychrometrics.boot}
            openNonce={psychrometrics.openNonce}
          />,
          document.body
        )}

      <AhuBoardFooter />
    </>
  );
};

export default AhuBoardPage;
